
import React from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, RotateCcw } from "lucide-react";
import { useRrwebSessions, RrwebSession } from '@/hooks/useRrwebSessions';
import SessionGrid from './SessionGrid';
import SessionPreview from './SessionPreview';

interface SessionSelectionPanelProps {
  selectedSessions: RrwebSession[];
  onSessionSelect: (session: RrwebSession) => void;
  onSessionDeselect: (session: RrwebSession) => void;
}

const SessionSelectionPanel = ({
  selectedSessions,
  onSessionSelect,
  onSessionDeselect
}: SessionSelectionPanelProps) => {
  const { data: sessions, isLoading } = useRrwebSessions();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [previewSession, setPreviewSession] = React.useState<RrwebSession | null>(null);

  const filteredSessions = React.useMemo(() => {
    if (!sessions) return [];
    
    return sessions.filter(session => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (session.name?.toLowerCase().includes(searchLower)) ||
        (session.description?.toLowerCase().includes(searchLower)) ||
        (session.initial_url?.toLowerCase().includes(searchLower))
      );
    });
  }, [sessions, searchTerm]);

  const handleSessionToggle = (session: RrwebSession) => {
    const isSelected = selectedSessions.some(s => s.id === session.id);
    if (isSelected) {
      onSessionDeselect(session);
    } else {
      onSessionSelect(session);
    }
  };

  const clearSelection = () => {
    selectedSessions.forEach(session => {
      onSessionDeselect(session);
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end">
        
        {selectedSessions.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearSelection}
            className="border-editor-light hover:bg-editor-light"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Clear ({selectedSessions.length})
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search sessions by name, description, or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-editor-dark border-editor-light text-white"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="border-editor-light hover:bg-editor-light"
        >
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {/* Selection Summary */}
      {selectedSessions.length > 0 && (
        <div className="bg-editor-accent/10 border border-editor-accent/20 rounded-lg p-3">
          <p className="text-sm text-editor-accent">
            <span className="font-medium">{selectedSessions.length}</span> session{selectedSessions.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex flex-wrap gap-1 mt-2">
            {selectedSessions.map(session => (
              <span
                key={session.id}
                className="inline-block bg-editor-accent/20 text-xs px-2 py-1 rounded text-editor-accent"
              >
                {session.name || `Session ${session.id.slice(0, 8)}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Sessions Grid */}
      <SessionGrid
        sessions={filteredSessions}
        selectedSessions={selectedSessions}
        onSelectSession={handleSessionToggle}
        onPreviewSession={setPreviewSession}
        isLoading={isLoading}
      />

      {/* Session Preview Modal */}
      <SessionPreview
        session={previewSession}
        open={!!previewSession}
        onOpenChange={(open) => !open && setPreviewSession(null)}
        onSelect={handleSessionToggle}
      />
    </div>
  );
};

export default SessionSelectionPanel;
